import { useMemo } from "react";
import { GlobalStyles, alpha, useTheme } from "@mui/material";

/**
 * Ponte entre o `ThemeProvider` (claro/escuro, paleta do tenant) e os tokens
 * estáticos de `pages/projects/designTokens.ts`. Os tokens exportam
 * `var(--pm-*)` em vez de hex fixo; aqui a gente recalcula essas variáveis
 * toda vez que o theme muda, então alternar claro/escuro (ou trocar de
 * tenant) atualiza a cor na hora, sem precisar tocar nos ~60 arquivos que
 * consomem `projectDetailTokens`.
 */
export const ProjectManagementThemeBridge = () => {
  const theme = useTheme();

  const vars = useMemo(() => {
    const isDark = theme.palette.mode === "dark";
    const soft = (color: string) => alpha(color, isDark ? 0.24 : 0.12);

    return {
      "--pm-primary": theme.palette.accentText,
      "--pm-primary-soft": soft(theme.palette.accentText),
      "--pm-danger": theme.palette.error.main,
      "--pm-danger-soft": soft(theme.palette.error.main),
      "--pm-warning": theme.palette.warning.main,
      "--pm-warning-soft": soft(theme.palette.warning.main),
      "--pm-success": theme.palette.success.main,
      "--pm-success-soft": soft(theme.palette.success.main),
      "--pm-info": theme.palette.info.main,
      "--pm-info-soft": soft(theme.palette.info.main),
      "--pm-neutral": theme.palette.text.secondary,
      "--pm-neutral-soft": alpha(theme.palette.text.primary, isDark ? 0.16 : 0.06),
      "--pm-text": theme.palette.text.primary,
      "--pm-text-muted": theme.palette.text.secondary,
      "--pm-text-faint": theme.palette.text.disabled,
      "--pm-border": theme.palette.divider,
      "--pm-bg": theme.palette.background.default,
      "--pm-card": theme.palette.background.paper,
    };
  }, [theme]);

  return <GlobalStyles styles={{ ":root": vars }} />;
};
